import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { InfoCircle } from "../../../assets";
import { ADMIN_TOKENS } from "../../../styles/adminTokens";
import { mixpanel } from "../../../utils/mixpanel";
import Tooltip from "./Tooltip";

type InfoTooltipButtonProps = {
  text: string;
  accessibilityLabel?: string;
  trackingEventName?: string;
  trackingProperties?: Record<string, unknown>;
};

const InfoTooltipButton: React.FC<InfoTooltipButtonProps> = ({
  text,
  accessibilityLabel = "Show more information",
  trackingEventName,
  trackingProperties,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const targetRef = React.useRef<View>(null);

  const handlePress = React.useCallback(() => {
    const nextIsVisible = !isVisible;
    if (nextIsVisible && trackingEventName) {
      mixpanel.track(trackingEventName, trackingProperties);
    }
    setIsVisible(nextIsVisible);
  }, [isVisible, trackingEventName, trackingProperties]);

  return (
    <View ref={targetRef} collapsable={false}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onPress={handlePress}
        style={({ pressed }) => [
          styles.button,
          pressed ? styles.buttonPressed : null,
        ]}
      >
        <View style={styles.iconBadge}>
          <InfoCircle size={14} color={ADMIN_TOKENS.color.textTertiary} />
        </View>
      </Pressable>
      {isVisible ? (
        <Tooltip
          targetRef={targetRef}
          text={text}
          onDismiss={() => setIsVisible(false)}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: ADMIN_TOKENS.radius.xxl,
  },
  buttonPressed: {
    opacity: 0.85,
  },
  iconBadge: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: ADMIN_TOKENS.radius.xxl,
  },
});

export default InfoTooltipButton;
